import routes from 'js/core_pre_webpack/routes';
import * as ApiUtils from 'js/utils/api_utils';
import {
  AdminSubmission,
  WaiverStatus,
  ApcPanelInfo,
} from 'js/journals/staff/types';
import { SubmissionModifiers, AdminSubmissionModifiers } from './schema';
import { SubmissionsFilter } from 'js/journals/staff/SubmissionFilterOptions';
import {
  Author,
  DisclosureAttribute,
  EditorialDecisions,
  OptionalEditorAttributes,
  ScopeCheckSignoffDecisions,
  ReviewRequest,
  DecisionTypes,
  DeferReasons,
  CreditTaxonomyRole,
  PaymentInformation,
  StripeCustomerInfo,
  BaseSerializedDecision,
} from 'js/letters/types';
import * as ReviewsApi from 'js/letters/apis/reviews_api';
import { UploadedAttachment } from 'js/letters/components/inputs/types';
import fetchWithCsrf from '../../../utils/fetch_with_csrf';
import { EmailTemplate } from 'js/journals/staff/custom_emails/CustomEmailEditor';

export function update(
  id: number,
  modifiers: SubmissionModifiers,
): Promise<{ success: boolean; last_updated_at: string }> {
  return ApiUtils.patch(
    routes.api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
    modifiers,
  );
}

export function finalize(
  id: number,
  serviceType: string | null,
): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.finalize_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
    { service_type: serviceType },
  );
}

export function requireRevisions(id: number): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.require_revisions_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function updateDownloadableFiles(
  submission_id: number,
  attachment_ids: number[],
): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.update_downloadable_files_api_v0_letters_submission_path({
      id: submission_id,
      subdomain_param: 'api',
    }),
    { attachment_ids },
  );
}

export function screen(id: number): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.screen_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function screen_revisions(id: number): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.screen_revisions_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function request_additional_author_revisions(
  id: number,
): Promise<{ success: boolean }> {
  return ApiUtils.put(
    routes.request_additional_author_revisions_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function screenReviewerRevisions(
  id: number,
): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.screen_reviewer_revisions_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function skip_to_stage(
  id: number,
  stage: string,
): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.skip_to_stage_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
      stage_name: stage,
    }),
  );
}

type AdminSubmissionListProps = {
  submissions: AdminSubmission[];
  hasMorePages: boolean;
  filters: SubmissionsFilter[];
  countByAuthor: Record<number, number>;
  filterCounts: Array<{ name: string; count: number }>;
};

export function adminJournalList(
  journalId: number,
  page: number,
  filters: string[],
  userId: string,
): Promise<AdminSubmissionListProps> {
  if (userId) {
    return ApiUtils.get(
      routes.journals_staff_submissions_path({
        journal_id: journalId,
        page,
        filters,
        user_id: userId,
      }),
    );
  } else
    return ApiUtils.get(
      routes.journals_staff_submissions_path({
        journal_id: journalId,
        page,
        filters,
      }),
    );
}

export function waiveCosts(id: number): Promise<{ success: boolean }> {
  const path = routes.waive_cost_api_v0_letters_submission_path({
    id: id,
    subdomain_param: 'api',
  });
  return ApiUtils.post(path);
}

export function verifyWaiver(
  id: number,
  selectedWaiverType: string,
  discountAmount: string,
  discountCurrency: string,
  staffNotes: string,
  sendAuthorEmail: boolean,
): Promise<{
  success: boolean;
  message?: string;
  waiver_status?: WaiverStatus;
}> {
  const path = routes.verify_waiver_api_v0_letters_submission_path({
    id: id,
    subdomain_param: 'api',
  });
  return ApiUtils.post(path, {
    selected_waiver_type: selectedWaiverType,
    discount_amount: discountAmount,
    discount_currency: discountCurrency,
    staff_notes: staffNotes,
    send_author_email: sendAuthorEmail,
  });
}

export function adminJournalShow(
  journalId: number,
  id: number,
): Promise<AdminSubmission> {
  return ApiUtils.get(
    routes.journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
  );
}

export function adminJournalUpdate(
  journalId: number,
  id: number,
  modifiers: AdminSubmissionModifiers,
) {
  return ApiUtils.patch(
    routes.journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    modifiers,
  );
}

export type UpdateDecisionProps = {
  decisionType:
    | 'scope_check'
    | 'editorial_decision'
    | 'ebm_revision'
    | 'quality_check'
    | 'scope_check_signoff'
    | 'editorial_quality'
    | 'editorial_quality_revision'
    | 'decision';
  description?: string;
  decision?: string;
  private_feedback?: string;
};

export function adminUpdateDecision(
  journalId: number,
  id: number,
  modifiers: UpdateDecisionProps,
) {
  return ApiUtils.patch(
    routes.edit_decision_journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    modifiers,
  );
}

export type ScopeCheckProps = {
  attributes: OptionalEditorAttributes;
  scope_check_decision?: ReviewsApi.ScopeCheckDecisions;
  scope_check_description?: string;
};

export function scopeCheckUpdate(
  journalId: number,
  id: number,
  modifiers: ScopeCheckProps,
) {
  return ApiUtils.patch(
    routes.scope_check_journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    modifiers,
  );
}

export function sendEbmRequests(
  submissionId: number,
  editorIds: number[] | null,
  decisionType: DecisionTypes,
  sendCancellationEmail = true,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.send_requests_api_v0_letters_ebm_requests_path({
      subdomain_param: 'api',
    }),
    {
      submission_id: submissionId,
      editor_ids: editorIds,
      decision_type: decisionType,
      send_cancellation_email: sendCancellationEmail,
    },
  );
}

export function cancelEbmRequest(
  requestId: number,
  requestType: string,
  copy: string,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.cancel_request_api_v0_letters_ebm_requests_path({
      subdomain_param: 'api',
    }),
    {
      request_id: requestId,
      request_type: requestType,
      copy: copy,
    },
  );
}

export type EditorialDecisionProps = {
  decision: EditorialDecisions;
  description: string;
  private_feedback?: string;
};

// scope check signoffs take in the same args as editorial decisions,
// but we specifically prohibit "to_defer"
export type ScopeCheckSignoffProps = EditorialDecisionProps & {
  decision: ScopeCheckSignoffDecisions;
};

export function scopeCheckSignoffUpdate(
  journalId: number,
  id: number,
  modifiers: ScopeCheckSignoffProps,
) {
  return ApiUtils.patch(
    routes.scope_check_signoff_journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    modifiers,
  );
}

export function editorialDecisionUpdate(
  journalId: number,
  id: number,
  modifiers: EditorialDecisionProps,
) {
  return ApiUtils.patch(
    routes.editorial_decision_journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    modifiers,
  );
}

export function finalDecision(
  journalId: number,
  id: number,
  modifiers: EditorialDecisionProps,
) {
  return ApiUtils.patch(
    routes.final_decision_journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    modifiers,
  );
}

export function publish(journalId: number, id: number) {
  return ApiUtils.patch(
    routes.publish_journals_staff_submission_path({
      journal_id: journalId,
      id,
    }),
    {},
  );
}

export function approveEditorialQualityDecision(options: {
  journalId: number;
  submissionId: number;
  editorDecision: BaseSerializedDecision | null;
  decision?: EditorialDecisions;
  description?: string;
  override?: boolean;
  template?: { edited: boolean } & EmailTemplate;
}): Promise<{ success: boolean; message?: string }> {
  const {
    journalId,
    submissionId,
    editorDecision,
    decision,
    description,
    override,
    template,
  } = options;
  const editorDecisionId = editorDecision?.id;
  const editorDecisionType = editorDecision?.decision_type;
  return ApiUtils.patch(
    routes.final_pm_signoff_decision_journals_staff_submission_path({
      journal_id: journalId,
      id: submissionId,
      subdomain_param: 'api',
    }),
    {
      editor_decision_id: editorDecisionId || null,
      editor_decision_type: editorDecisionType || null,
      decision,
      description,
      override,
      template,
    },
  );
}

export function approveScopeCheckDecision(
  journalId: number,
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.patch(
    routes.scope_check_pm_approval_journals_staff_submission_path({
      journal_id: journalId,
      id,
      subdomain_param: 'api',
    }),
    {
      approved: true,
    },
  );
}

export function approveScopeCheckSignoffDecision(
  journalId: number,
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.patch(
    routes.scope_check_signoff_pm_approval_journals_staff_submission_path({
      journal_id: journalId,
      id,
      subdomain_param: 'api',
    }),
    {
      approved: true,
    },
  );
}

export function updateDeferReason(
  journalId: number,
  id: number,
  decisionId: number,
  decisionType: DecisionTypes,
  deferReason: DeferReasons,
) {
  return ApiUtils.patch(
    routes.update_defer_reason_journals_staff_submission_path({
      journal_id: journalId,
      id,
      subdomain_param: 'api',
    }),
    {
      decision_id: decisionId,
      decision_type: decisionType,
      defer_reason: deferReason,
    },
  );
}

export type AuthorCamelCase = {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  primary?: boolean | null;
  institution?: string | null;
  phoneNumber?: string | null;
  orcidUrl?: string | null;
  institutionUrl?: string | null;
  scopusUrl?: string | null;
  id?: number | null;
  authorProfileUrl?: string | null;
  correspondingAuthor?: DisclosureAttribute;
  country?: string | null;
  authorTaxonomy?: CreditTaxonomyRole[];
};

export function formatAuthors(authors: Array<AuthorCamelCase>): Array<Author> {
  return authors.map((author) => ({
    first_name: author.firstName,
    last_name: author.lastName,
    email: author.email,
    institution: author.institution,
    title: author.title,
    phone_number: author.phoneNumber,
    primary: author.primary,
    orcid_url: author.orcidUrl,
    institution_url: author.institutionUrl,
    scopus_url: author.scopusUrl,
    id: author.id,
    corresponding_author: author.correspondingAuthor,
    country: author.country || '',
    author_taxonomy: author.authorTaxonomy,
  }));
}

export function draftUpdatedAt(
  obscured_id: string,
): Promise<{ updated_at: Date }> {
  return ApiUtils.get(
    routes.draft_last_updated_api_v0_letters_submissions_path({
      obscured_id,
      subdomain_param: 'api',
    }),
  );
}

export function requestPlagiarismReport(
  id: number,
): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.create_api_submission_api_v0_turnitin_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function get_viewer_launcher_url(
  id: number,
): Promise<{ success: boolean; result?: string }> {
  return ApiUtils.get(
    routes.get_viewer_launcher_url_api_v0_turnitin_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function updateRevisionAttachment(
  submissionId: number,
  attachmentId: number,
  supportingFile: boolean,
) {
  return ApiUtils.post(
    routes.update_revision_attachment_api_v0_letters_revision_rounds_path({
      subdomain_param: 'api',
    }),
    {
      submission_id: submissionId,
      attachment_id: attachmentId,
      supporting_file: supportingFile,
    },
  );
}

export function updateRevisionLatexZip(
  submissionId: number,
  attachmentId: number,
) {
  return ApiUtils.post(
    routes.update_revision_latex_zip_api_v0_letters_revision_rounds_path({
      subdomain_param: 'api',
    }),
    {
      submission_id: submissionId,
      attachment_id: attachmentId,
    },
  );
}

export function updateRevisionSupportingFile(
  submissionId: number,
  newAdditionalFiles: UploadedAttachment[],
) {
  return ApiUtils.post(
    routes.update_revision_additional_files_api_v0_letters_revision_rounds_path(
      {
        subdomain_param: 'api',
      },
    ),
    {
      submission_id: submissionId,
      new_additional_files: newAdditionalFiles,
    },
  );
}

export function updateRevisionComment(
  revisionId: number,
  comment: string,
  commentType: string,
) {
  return ApiUtils.post(
    routes.update_revision_comment_api_v0_letters_revision_rounds_path({
      subdomain_param: 'api',
    }),
    {
      revision_id: revisionId,
      comment: comment,
      comment_type: commentType,
    },
  );
}

export function submitRevisions(submissionId: number, revisionId: number) {
  return ApiUtils.post(
    routes.submit_revisions_api_v0_letters_revision_rounds_path({
      subdomain_param: 'api',
    }),
    {
      submission_id: submissionId,
      revision_id: revisionId,
    },
  );
}

export interface ReviewRequestsResponse {
  success: boolean;
  review_requests: ReviewRequest[];
}

export function getReviewRequests(
  revisionRoundId: number,
): Promise<ReviewRequestsResponse> {
  return ApiUtils.get(
    routes.review_requests_api_v0_letters_revision_rounds_path({
      revision_round_id: revisionRoundId,
      subdomain_param: 'api',
    }),
  );
}

export type preSelectDecisionTypes = 'regular_decision' | 'signoff';
export function preSelectEditorsForDecision(
  id: number,
  editorIds: number[],
  decisionType: preSelectDecisionTypes,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.pre_select_editors_for_decision_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
    { editor_ids: editorIds, decision_type: decisionType },
  );
}

export function requestEditorialQualityRevisions(
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.request_editorial_quality_revisions_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function blockProduction(
  id: number,
  block: boolean,
): Promise<{ success: boolean; blocked: boolean }> {
  return ApiUtils.post(
    routes.block_production_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
    { block_production: block },
  );
}

export function blockEmails(
  id: number,
  block: boolean,
): Promise<{ success: boolean; blocked: boolean }> {
  return ApiUtils.post(
    routes.block_emails_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
    { block },
  );
}

export function poke(
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.poke_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function assignSubmission(
  submissionId: number,
  journalId: number,
  userId: number | undefined,
): Promise<{ success: boolean }> {
  return ApiUtils.patch(
    routes.assign_staff_member_journals_staff_submission_path({
      id: submissionId,
      journal_id: journalId,
      subdomain_param: 'api',
    }),
    {
      user_id: userId,
    },
  );
}

export interface StaffMemberWithCounts {
  user_id: number;
  display_name: string;
  total_assignments: number;
  journal_assignments: number;
}

export interface StaffMembersResponse {
  success: boolean;
  staff_members: StaffMemberWithCounts[];
}

export function getStaffMembers(
  submissionId: number,
): Promise<StaffMembersResponse> {
  return ApiUtils.get(
    routes.get_staff_members_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
  );
}

export function clearReviewRequestErrors(
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.clear_review_request_errors_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function clearReviewRequestStatus(
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.clear_review_request_status_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export async function uploadImage(
  id: number,
  images: FileList,
  setLoading: (loading: boolean) => void,
): Promise<Response> {
  setLoading(true);
  const formData = new FormData();
  for (let i = 0; i < images.length; i++) {
    formData.append('image[]', images[i]);
  }

  const response = await fetchWithCsrf(
    routes.update_submission_images_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      body: formData,
    },
  );
  if (response.ok) {
    setLoading(false);
    alert('Successfully uploaded files!');
  } else {
    setLoading(false);
    let error: string;
    try {
      error = (await response.json()).error;
    } catch {
      error = `Couldn't upload your image. Please try again.`;
    }
    alert(error);
  }
  return await response.json();
}

export function triggerJatsJob(id: number): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.trigger_jats_transform_api_v0_letters_submission_path({
      id,
      subdomain_param: 'api',
    }),
  );
}

export function requestAuthorCompletion(
  submissionId: number,
  instructions: string,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.put(
    routes.request_author_completion_api_v0_letters_submission_url({
      id: submissionId,
      subdomain_param: 'api',
    }),
    { instructions },
  );
}

export function setApcCurrency(
  submissionId: number,
  currency: string,
): Promise<{ success: boolean; message?: string }> {
  return ApiUtils.post(
    routes.update_apc_currency_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    { currency },
  );
}

export function finalizeAndSendInvoiceEmail(
  journalId: number,
  submissionId: number,
): Promise<{ success: boolean }> {
  return ApiUtils.post(
    routes.finalize_and_send_invoice_email_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    {},
  );
}

// Types for cited authors functionality
export type CitedAuthor = {
  id: string;
  email: string;
  name: string;
  found_name?: string;
  source: 'academia_user' | 'offsite_user' | 'openalex' | 'not_found';
  user_id?: number;
  profile_url?: string;
  offsite_user_id?: number;
  openalex_work_id?: string;
  openalex_author_id?: string;
  match_confidence?: number;
  cited_title?: string;
  cited_journal?: string;
  cited_doi?: string;
  already_sent: boolean;
  marked_as_mismatch?: boolean;
};

export type CitationGroup = {
  citation: {
    title: string;
    journal?: string;
    doi?: string;
    year?: number;
    index: number;
  };
  authors: CitedAuthor[];
};

export type CitedAuthorsPreviewResponse = {
  success: boolean;
  citations_count?: number;
  authors?: CitedAuthor[];
  citation_groups?: CitationGroup[];
  stats?: {
    total_authors: number;
    emails_found: number;
    academia_users: number;
    external_emails: number;
    no_email_found: number;
  };
  message?: string;
  details?: string[];
  error?: string;
  processing?: boolean;
  status?: string;
  error_type?: 'citation_extraction' | 'email_resolution' | 'letters_error';
};

export type CitedAuthorsSendResponse = {
  success: boolean;
  emails_sent: number;
  emails_failed: number;
  duplicates_skipped: number;
  message?: string;
  error?: string;
};

// API functions for cited authors
export function previewCitedAuthors(
  journalId: number,
  submissionId: number,
  retry?: boolean,
): Promise<CitedAuthorsPreviewResponse> {
  return ApiUtils.post(
    routes.cited_authors_preview_journals_staff_submission_path({
      journal_id: journalId,
      id: submissionId,
    }),
    retry ? { retry: 'true' } : {},
  );
}

export function sendCitedAuthorEmails(
  journalId: number,
  submissionId: number,
  selectedAuthors: CitedAuthor[],
): Promise<CitedAuthorsSendResponse> {
  return ApiUtils.post(
    routes.cited_authors_send_journals_staff_submission_path({
      journal_id: journalId,
      id: submissionId,
    }),
    {
      selected_authors: selectedAuthors,
    },
  );
}

export type CitedAuthorsStatusResponse = {
  status:
    | 'processing'
    | 'completed'
    | 'failed'
    | 'not_started'
    | 'starting'
    | 'completed_with_errors'
    | 'unknown';
  message?: string;
  updated_at?: string;
};

export function getCitedAuthorsStatus(
  journalId: number,
  submissionId: number,
): Promise<CitedAuthorsStatusResponse> {
  return ApiUtils.get(
    routes.cited_authors_status_journals_staff_submission_path({
      journal_id: journalId,
      id: submissionId,
    }),
  );
}

export type MismatchReasonType = 'wrong_user' | 'wrong_email' | 'other';

export type CitedAuthorMismatchResponse = {
  success: boolean;
  message?: string;
  error?: string;
};

export function markCitedAuthorAsMismatch(
  journalId: number,
  submissionId: number,
  authorId: string,
  reason?: MismatchReasonType,
): Promise<CitedAuthorMismatchResponse> {
  return ApiUtils.post(
    routes.cited_authors_mismatch_journals_staff_submission_path({
      journal_id: journalId,
      id: submissionId,
    }),
    {
      author_id: authorId,
      reason: reason,
    },
  );
}

export function undoCitedAuthorMismatch(
  journalId: number,
  submissionId: number,
  authorId: string,
): Promise<CitedAuthorMismatchResponse> {
  const baseUrl =
    routes.undo_cited_authors_mismatch_journals_staff_submission_path({
      journal_id: journalId,
      id: submissionId,
    });
  const urlWithParams = `${baseUrl}?author_id=${encodeURIComponent(authorId)}`;
  return ApiUtils.deleteRequest(urlWithParams).then((response) =>
    response.json(),
  );
}

type GenerateAiTranslationsParams = {
  language: string;
  journal_id: number;
  model?: string;
};

type GenerateAiTranslationsResponse = {
  translation: string;
};

export async function generateAiTranslations(
  submissionId: string,
  params: GenerateAiTranslationsParams,
): Promise<GenerateAiTranslationsResponse> {
  const response = await fetchWithCsrf(
    routes.generate_ai_translations_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        submission_id: submissionId,
        ...params,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to generate translation');
  }

  return await response.json();
}

type ApproveAiTranslationParams = {
  language: string;
  journal_id: number;
};

type ApproveAiTranslationResponse = {
  success: boolean;
};

export async function approveAiTranslation(
  submissionId: string,
  params: ApproveAiTranslationParams,
): Promise<ApproveAiTranslationResponse> {
  const response = await fetchWithCsrf(
    routes.approve_ai_translation_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        submission_id: submissionId,
        ...params,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to approve translation');
  }

  return await response.json();
}

export async function unapproveAiTranslation(
  submissionId: string,
  params: ApproveAiTranslationParams,
): Promise<ApproveAiTranslationResponse> {
  const response = await fetchWithCsrf(
    routes.unapprove_ai_translation_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        submission_id: submissionId,
        ...params,
      }),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to unapprove translation');
  }

  return await response.json();
}

// Reference Evaluation types and API
export type ReferenceEvaluationResponse = {
  success: boolean;
  evaluation_id?: number;
  evaluation_url?: string;
  status?: string;
  error?: string;
};

export async function generateReferenceEvaluation(
  journalId: number,
  submissionId: number,
): Promise<ReferenceEvaluationResponse> {
  const response = await fetchWithCsrf(
    routes.generate_reference_evaluation_journals_staff_submission_url({
      journal_id: journalId,
      id: submissionId,
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return await response.json();
}

// Revision Request Deadline Adjustment
export type UpdateDeadlineResponse = {
  success: boolean;
  new_deadline?: string;
  error?: string;
};

export function updateRevisionRequestDeadline(
  requestId: number,
  newDueBy: string,
  reason: string,
): Promise<UpdateDeadlineResponse> {
  return ApiUtils.patch(
    routes.update_deadline_api_v0_letters_revision_request_path({
      id: requestId,
      subdomain_param: 'api',
    }),
    {
      new_due_by: newDueBy,
      reason,
    },
  );
}

type ConfirmAuthorWaiverStatusResponse = {
  author_confirmed_at: string;
};
export function confirmAuthorWaiverStatus(
  submissionId: number,
  waiverStatusId: number,
): Promise<ConfirmAuthorWaiverStatusResponse> {
  return ApiUtils.post(
    routes.api_v0_letters_waiver_status_confirm_path({
      id: submissionId,
      waiver_status_id: waiverStatusId,
    }),
  );
}

type SendWaiverStatusConfirmationEmailResponse = {
  author_confirmation_email_sent_at: string;
};
export function sendWaiverStatusConfirmationEmail(
  submissionId: number,
  waiverStatusId: number,
): Promise<SendWaiverStatusConfirmationEmailResponse> {
  return ApiUtils.post(
    routes.api_v0_letters_waiver_status_send_email_path({
      id: submissionId,
      waiver_status_id: waiverStatusId,
    }),
  );
}
export function updatePaymentInfo(
  submissionId: number,
  paymentInfo: PaymentInformation,
): Promise<{
  success: boolean;
  stripe_customer_info?: StripeCustomerInfo;
  error?: string;
}> {
  return ApiUtils.post(
    routes.payment_info_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    { payment_info: paymentInfo },
  );
}

export function getApcPanelInfo(submissionId: number): Promise<ApcPanelInfo> {
  return ApiUtils.get(
    routes.overview_api_v0_letters_waiver_statuses_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
  );
}

export type AiTranslationInteractionType = 'view' | 'select';

export type TrackAiTranslationInteractionParams = {
  language: string;
  interaction_type: AiTranslationInteractionType;
  source: string;
};

export async function trackAiTranslationInteraction(
  submissionId: string,
  params: TrackAiTranslationInteractionParams,
): Promise<void> {
  await fetchWithCsrf(
    routes.track_ai_translation_interaction_api_v0_letters_submission_path({
      id: submissionId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        submission_id: submissionId,
        ...params,
      }),
    },
  );
}
